import {
    previewContainer,
    previewDisplayText,
    previewDisplayTextTypeRampFull,
    previewDisplayTextTextFlow,
    previewLinesContainer,
    previewLinesContainerTextFlow,
    previewLinesDisplayText,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import classnames from 'owa-classnames';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { tokens } from '@fluentui/react-components';
import { observer } from 'owa-mobx-react';
import { highlightTermsInHtmlElement } from 'owa-highlight';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import type { Accuracy } from 'owa-mark/lib/utils/markElements';
import React from 'react';
import CopilotPrioritizeOnboardedCallout from './CopilotPrioritizeOnboardedCallout';
import { getStore as getMailListStore } from 'owa-mail-list-store';
import type { MailboxInfo } from 'owa-client-types';
import { isPrioritizeOnDeliveryOn } from 'owa-copilot-settings-utils';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

export interface MailListItemPreviewDisplayProps {
    highlightTerms: string[];
    previewDisplay: string;
    highlightAccuracy?: Accuracy;
    rowId: string;
    mailboxInfo: MailboxInfo;
    copilotPrioritizeOnDeliveryOn?: boolean;
    isSelected: boolean;
    cfTextStyle?: React.CSSProperties;
    numMessagePreviewLines: number;
    useTextFlowLayout?: boolean;
}

const defaultExport: React.FunctionComponent<MailListItemPreviewDisplayProps> = observer(
    function MailListItemPreviewDisplay(props: MailListItemPreviewDisplayProps) {
        const {
            highlightTerms,
            previewDisplay,
            highlightAccuracy,
            rowId,
            cfTextStyle,
            isSelected,
            numMessagePreviewLines,
            useTextFlowLayout,
        } = props;
        const shouldShowPreviewLinesStyles = isGlobalFeatureEnabled('tri-mlMultiLinePreview');
        const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
        const rowRef = React.useRef<HTMLDivElement>(null);
        const targetRowId = getMailListStore().copilotPrioritizeOnboardedCalloutTarget;

        const highLightTerms = React.useCallback(
            (element: HTMLElement) => {
                highlightTermsInHtmlElement(
                    element,
                    highlightTerms,
                    false /* separateWordSearch */,
                    highlightAccuracy /* highlightAccuracy */
                );
            },
            [highlightTerms, highlightAccuracy]
        );

        const textStyle = React.useMemo(() => {
            if (cfTextStyle) {
                const baseColor = isSelected
                    ? tokens.colorNeutralForeground2
                    : tokens.colorNeutralForeground3;

                return {
                    color: `var(--cfHoverTextColorOverride, ${baseColor})`,
                };
            }
            return {};
        }, [cfTextStyle, isSelected]);

        const containerClassName = classnames(
            shouldShowPreviewLinesStyles ? previewLinesContainer : previewContainer,
            useTextFlowLayout && previewLinesContainerTextFlow
        );
        const spanClassName = classnames(
            shouldShowPreviewLinesStyles ? previewLinesDisplayText : previewDisplayText,
            useTextFlowLayout && previewDisplayTextTextFlow,
            useMessageListTextStyle('Major'),
            isTypeRampUpdatesEnabled &&
                getDensityModeCssClass(previewDisplayTextTypeRampFull, undefined, undefined)
        );

        return (
            <div className={containerClassName} ref={rowRef}>
                <span
                    key={numMessagePreviewLines}
                    className={spanClassName}
                    ref={highLightTerms}
                    style={textStyle}
                >
                    {previewDisplay}
                </span>
                {targetRowId === rowId &&
                    (props.copilotPrioritizeOnDeliveryOn ??
                        isPrioritizeOnDeliveryOn(props.mailboxInfo)) && (
                        <CopilotPrioritizeOnboardedCallout
                            target={rowRef}
                            id="CopilotPrioritizeOnboarded"
                        />
                    )}
            </div>
        );
    },
    'MailListItemPreviewDisplay'
);
export default defaultExport;
